import { zodResolver } from "@hookform/resolvers/zod"
import { ArrowLeft } from "react-feather"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router"
import * as z from "zod"
import { useChangePassword } from "@/api/client/auth/hooks"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { toast } from "@/hooks/use-toast"

const passwordChangeFormSchema = z
  .object({
    currentPassword: z.string().min(1, "現在のパスワードを入力してください"),
    newPassword: z
      .string()
      .min(8, "8文字以上で入力してください")
      .regex(/^[0-9A-Za-z]+$/, "半角英数字のみで入力してください"),
    confirmPassword: z.string().min(1, "確認用パスワードを入力してください"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "パスワードが一致しません",
    path: ["confirmPassword"],
  })

type PasswordChangeFormValues = z.infer<typeof passwordChangeFormSchema>

export function PasswordChangePage() {
  const navigate = useNavigate()
  const changePasswordMutation = useChangePassword({
    onError: () =>
      toast({
        title: "パスワードの変更に失敗しました",
        description: "現在のパスワードが正しいかご確認ください",
        variant: "destructive",
      }),
  })

  const form = useForm<PasswordChangeFormValues>({
    resolver: zodResolver(passwordChangeFormSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  })

  const onSubmit = (data: PasswordChangeFormValues) => {
    changePasswordMutation.mutate(
      {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      },
      {
        onSuccess: () => {
          toast({
            title: "パスワードを変更しました",
          })
          navigate("/profile")
        },
      }
    )
  }

  const handleCancel = () => {
    navigate("/profile")
  }

  const handleBack = () => {
    navigate("/profile")
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-6">
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center justify-center p-1.5 bg-white rounded-[5px] shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)] size-7 hover:bg-slate-50"
        >
          <ArrowLeft size={14} className="text-[#3f4652]" />
        </button>
        <h1 className="text-sm font-semibold text-[#3f4652]">プロフィール編集</h1>
      </div>

      {/* Form Card */}
      <div className="flex flex-col gap-4 w-full max-w-[895px]">
        <div className="shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]">
          {/* Card Header */}
          <div className="flex items-center gap-4 px-5 py-2.5 h-[52px] bg-white border-b border-slate-200 rounded-t-[10px]">
            <span className="text-sm font-semibold text-[#3f4652]">パスワード</span>
          </div>

          {/* Card Body */}
          <div className="bg-white p-5 rounded-b-[10px]">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                {/* 現在のパスワード */}
                <FormField
                  control={form.control}
                  name="currentPassword"
                  render={({ field }) => (
                    <FormItem className="w-[265px]">
                      <FormLabel className="text-xs font-semibold text-[#3f4652]">
                        現在のパスワード
                      </FormLabel>
                      <FormControl>
                        <Input type="password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* 新しいパスワード */}
                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem className="w-[265px]">
                      <FormLabel className="text-xs font-semibold text-[#3f4652]">
                        新しいパスワード
                      </FormLabel>
                      <FormControl>
                        <Input type="password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* 新しいパスワード（確認用） */}
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem className="w-[265px]">
                      <FormLabel className="text-xs font-semibold text-[#3f4652]">
                        新しいパスワード（確認用）
                      </FormLabel>
                      <FormControl>
                        <Input type="password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Spacer for layout alignment */}
                <div className="h-[53px]" />
              </form>
            </Form>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="submit"
            onClick={form.handleSubmit(onSubmit)}
            loading={changePasswordMutation.isPending}
          >
            保存する
          </Button>
          <Button variant="outline" onClick={handleCancel}>
            キャンセル
          </Button>
        </div>
      </div>
    </div>
  )
}
